import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { MilestoneRaffle, PeriodicRaffle } from '@infinex/crate-config';
import { createRaffleKey, createRaffleMap } from '@infinex/crate-config';
import { useDataset } from '@infinex/user';

import { getTRPCClient } from '../../../api/config';
import type { PrizeDrawWinActivity } from '../../../types';
import { ActivityStatus, ActivityType } from '../../../types';

export const useRaffleWinActivities = () => {
  const trpc = getTRPCClient();

  // Get raffle wins from dataset
  const { data: rafflePrizes, status: rafflePrizesStatus } =
    useDataset('crateRafflePrizes');
  const { data: raffles, isSuccess: rafflesSuccess } = useQuery(
    trpc.crates.getPrizeDraws.queryOptions(undefined)
  );

  const isLoading =
    rafflePrizesStatus === 'loading' ||
    rafflePrizesStatus === 'idle' ||
    !rafflesSuccess;

  const raffleMap = useMemo(
    () =>
      createRaffleMap([
        ...((raffles?.milestone ?? []) as MilestoneRaffle[]),
        ...((raffles?.periodic ?? []) as PeriodicRaffle[]),
      ]),
    [raffles]
  );

  const raffleWinActivities: PrizeDrawWinActivity[] = useMemo(() => {
    return (
      rafflePrizes?.wins.flatMap((raffleWin) => {
        const raffle = raffleMap.get(
          createRaffleKey(raffleWin.prizeDrawId, raffleWin.prizeDrawType)
        );

        if (!raffle) {
          return [];
        }
        return raffleWin.prizes.map((prize) => {
          const prizeIndex = raffleWin.prizes.indexOf(prize);
          return {
            id: `${raffleWin.id}-${prizeIndex}`,
            status: raffleWin.fulfilledAt
              ? ActivityStatus.SUCCESS
              : ActivityStatus.PROCESSING,
            timestamp: new Date(raffleWin.createdAt),
            type: ActivityType.PRIZE_DRAW_WIN,
            prizeDraw: raffle,
            prize: prize,
            transactionHash: raffleWin.txHash ?? undefined,
            ticketsHeld: raffleWin.ticketsHeld,
            position: raffleWin.position,
            fulfilledAt: raffleWin.fulfilledAt ?? undefined,
          };
        });
      }) ?? []
    );
  }, [raffleMap, rafflePrizes]);

  return {
    raffleWinActivities,
    isLoading,
  };
};
